import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["admin"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      adminTemplatesList: build.query<
        AdminTemplatesListApiResponse,
        AdminTemplatesListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/templates/`,
          params: {
            category: queryArg.category,
            firm_id: queryArg.firmId,
            format: queryArg.format,
            include_deleted: queryArg.includeDeleted,
            is_default: queryArg.isDefault,
            is_global: queryArg.isGlobal,
            limit: queryArg.limit,
            offset: queryArg.offset,
            ordering: queryArg.ordering,
            scope: queryArg.scope,
            search: queryArg.search,
            status: queryArg.status,
          },
        }),
        providesTags: ["admin"],
      }),
      adminTemplatesCreate: build.mutation<
        AdminTemplatesCreateApiResponse,
        AdminTemplatesCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/templates/`,
          method: "POST",
          body: queryArg.adminTemplate,
        }),
        invalidatesTags: ["admin"],
      }),
      adminTemplatesRetrieve: build.query<
        AdminTemplatesRetrieveApiResponse,
        AdminTemplatesRetrieveApiArg
      >({
        query: (queryArg) => ({ url: `/api/admin/templates/${queryArg.id}/` }),
        providesTags: ["admin"],
      }),
      adminTemplatesPartialUpdate: build.mutation<
        AdminTemplatesPartialUpdateApiResponse,
        AdminTemplatesPartialUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/templates/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedAdminTemplate,
        }),
        invalidatesTags: ["admin"],
      }),
      adminTemplatesSoftDelete: build.mutation<
        AdminTemplatesSoftDeleteApiResponse,
        AdminTemplatesSoftDeleteApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/templates/${queryArg.id}/soft_delete/`,
          method: "POST",
          body: queryArg.adminTemplate,
        }),
        invalidatesTags: ["admin"],
      }),
      adminTemplatesResolveDefault: build.query<
        AdminTemplatesResolveDefaultApiResponse,
        AdminTemplatesResolveDefaultApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/templates/resolve_default/`,
          params: {
            category: queryArg.category,
            firm_id: queryArg.firmId,
          },
        }),
        providesTags: ["admin"],
      }),
      adminTemplatesSetDefault: build.mutation<
        AdminTemplatesSetDefaultApiResponse,
        AdminTemplatesSetDefaultApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/templates/set_default/`,
          method: "POST",
          body: queryArg.setDefaultSchema,
        }),
        invalidatesTags: ["admin"],
      }),
      adminTemplatesUnsetDefault: build.mutation<
        AdminTemplatesUnsetDefaultApiResponse,
        AdminTemplatesUnsetDefaultApiArg
      >({
        query: (queryArg) => ({
          url: `/api/admin/templates/unset_default/`,
          method: "POST",
          body: queryArg.setDefaultSchema,
        }),
        invalidatesTags: ["admin"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type AdminTemplatesListApiResponse =
  /** status 200  */ PaginatedAdminTemplateListRead;
export type AdminTemplatesListApiArg = {
  /** Filter by template category */
  category?: string;
  /** Filter by exact firm ID */
  firmId?: string;
  /** Filter by template format */
  format?: "DOCX" | "EXTERNAL" | "HTML" | "PDF" | "SMS";
  /** Include soft-deleted templates */
  includeDeleted?: boolean;
  /** Filter by default status */
  isDefault?: boolean;
  /** Filter for global templates (firm_id is null) */
  isGlobal?: boolean;
  /** Number of results to return per page. */
  limit?: number;
  /** The initial index from which to return the results. */
  offset?: number;
  /** Order by field (prefix with - for desc). Options: name, firm_name, category, modified_at, created_at */
  ordering?: string;
  /** Filter by scope (GLOBAL or FIRM) */
  scope?: "FIRM" | "GLOBAL";
  /** Search by name or firm name */
  search?: string;
  /** * `ACTIVE` - ACTIVE
   * `SUPERSEDED` - SUPERSEDED
   * `ARCHIVED` - ARCHIVED */
  status?: "ACTIVE" | "ARCHIVED" | "SUPERSEDED";
};
export type AdminTemplatesCreateApiResponse =
  /** status 201  */ AdminTemplateRead;
export type AdminTemplatesCreateApiArg = {
  adminTemplate: AdminTemplate;
};
export type AdminTemplatesRetrieveApiResponse =
  /** status 200  */ AdminTemplateRead;
export type AdminTemplatesRetrieveApiArg = {
  /** A UUID string identifying this template. */
  id: string;
};
export type AdminTemplatesPartialUpdateApiResponse =
  /** status 200  */ AdminTemplateRead;
export type AdminTemplatesPartialUpdateApiArg = {
  /** A UUID string identifying this template. */
  id: string;
  patchedAdminTemplate: PatchedAdminTemplate;
};
export type AdminTemplatesSoftDeleteApiResponse =
  /** status 200  */ AdminTemplateRead;
export type AdminTemplatesSoftDeleteApiArg = {
  /** A UUID string identifying this template. */
  id: string;
  adminTemplate: AdminTemplate;
};
export type AdminTemplatesResolveDefaultApiResponse =
  /** status 200  */ ReadTemplateResolution;
export type AdminTemplatesResolveDefaultApiArg = {
  /** Template category */
  category: string;
  /** Optional firm ID for firm-specific resolution */
  firmId?: string;
};
export type AdminTemplatesSetDefaultApiResponse =
  /** status 200  */ AdminTemplateRead;
export type AdminTemplatesSetDefaultApiArg = {
  setDefaultSchema: SetDefaultSchema;
};
export type AdminTemplatesUnsetDefaultApiResponse =
  /** status 200  */ AdminTemplateRead;
export type AdminTemplatesUnsetDefaultApiArg = {
  setDefaultSchema: SetDefaultSchema;
};
export type AdminTemplate = {};
export type AdminTemplateRead = {
  id: string;
  created_at: string;
  modified_at: string;
  name: string;
  display_name: string | null;
  description: string | null;
  /** * `ACTIVE` - Active
   * `SUPERSEDED` - Superseded
   * `ARCHIVED` - Archived */
  status: "ACTIVE" | "SUPERSEDED" | "ARCHIVED";
  /** * `HIPAA_COVERSHEET` - Hipaa Coversheet
   * `HIPAA_AUTHORIZATION` - Hipaa Authorization
   * `FOIA_LETTER` - Foia Letter
   * `LIEN_NOTICE` - Lien Notice
   * `RETAINER` - Retainer
   * `LOR_GENERIC` - Lor Generic
   * `LOR_FIRST_PARTY` - Lor First Party
   * `LOR_THIRD_PARTY` - Lor Third Party
   * `LOR_HEALTH` - Lor Health
   * `WELCOME_LETTER` - Welcome Letter
   * `DROP_LETTER` - Drop Letter
   * `JOINT_VENTURE_NOTICE` - Joint Venture Notice
   * `CLIENT_COMMUNICATION` - Client Communication
   * `MEDICAL_RECORDS_REQUEST` - Medical Records Request
   * `AFFIDAVIT` - Affidavit
   * `LETTER_OF_PROTECTION` - Letter Of Protection
   * `PRESERVATION_LETTER` - Preservation Letter
   * `PIP_DEMAND` - Pip Demand
   * `OTHER` - Other */
  category:
    | "HIPAA_COVERSHEET"
    | "HIPAA_AUTHORIZATION"
    | "FOIA_LETTER"
    | "LIEN_NOTICE"
    | "RETAINER"
    | "LOR_GENERIC"
    | "LOR_FIRST_PARTY"
    | "LOR_THIRD_PARTY"
    | "LOR_HEALTH"
    | "WELCOME_LETTER"
    | "DROP_LETTER"
    | "JOINT_VENTURE_NOTICE"
    | "CLIENT_COMMUNICATION"
    | "MEDICAL_RECORDS_REQUEST"
    | "AFFIDAVIT"
    | "LETTER_OF_PROTECTION"
    | "PRESERVATION_LETTER"
    | "PIP_DEMAND"
    | "OTHER";
  /** * `PDF` - Pdf
   * `DOCX` - Docx
   * `HTML` - Html
   * `SMS` - Sms
   * `EXTERNAL` - External */
  format: "PDF" | "DOCX" | "HTML" | "SMS" | "EXTERNAL";
  /** * `GLOBAL` - Global
   * `FIRM` - Firm */
  scope: "GLOBAL" | "FIRM";
  firm_id: string | null;
  firm_name: string | null;
  is_global: boolean;
  is_default: boolean;
  is_deleted: boolean;
  deleted_at: string | null;
};
export type PaginatedAdminTemplateList = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: AdminTemplate[];
};
export type PaginatedAdminTemplateListRead = {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: AdminTemplateRead[];
};
export type PatchedAdminTemplate = {};
export type PatchedAdminTemplateRead = {
  id?: string;
  created_at?: string;
  modified_at?: string;
  name?: string;
  display_name?: string | null;
  description?: string | null;
  /** * `ACTIVE` - Active
   * `SUPERSEDED` - Superseded
   * `ARCHIVED` - Archived */
  status?: "ACTIVE" | "SUPERSEDED" | "ARCHIVED";
  /** * `HIPAA_COVERSHEET` - Hipaa Coversheet
   * `HIPAA_AUTHORIZATION` - Hipaa Authorization
   * `FOIA_LETTER` - Foia Letter
   * `LIEN_NOTICE` - Lien Notice
   * `RETAINER` - Retainer
   * `LOR_GENERIC` - Lor Generic
   * `LOR_FIRST_PARTY` - Lor First Party
   * `LOR_THIRD_PARTY` - Lor Third Party
   * `LOR_HEALTH` - Lor Health
   * `WELCOME_LETTER` - Welcome Letter
   * `DROP_LETTER` - Drop Letter
   * `JOINT_VENTURE_NOTICE` - Joint Venture Notice
   * `CLIENT_COMMUNICATION` - Client Communication
   * `MEDICAL_RECORDS_REQUEST` - Medical Records Request
   * `AFFIDAVIT` - Affidavit
   * `LETTER_OF_PROTECTION` - Letter Of Protection
   * `PRESERVATION_LETTER` - Preservation Letter
   * `PIP_DEMAND` - Pip Demand
   * `OTHER` - Other */
  category?:
    | "HIPAA_COVERSHEET"
    | "HIPAA_AUTHORIZATION"
    | "FOIA_LETTER"
    | "LIEN_NOTICE"
    | "RETAINER"
    | "LOR_GENERIC"
    | "LOR_FIRST_PARTY"
    | "LOR_THIRD_PARTY"
    | "LOR_HEALTH"
    | "WELCOME_LETTER"
    | "DROP_LETTER"
    | "JOINT_VENTURE_NOTICE"
    | "CLIENT_COMMUNICATION"
    | "MEDICAL_RECORDS_REQUEST"
    | "AFFIDAVIT"
    | "LETTER_OF_PROTECTION"
    | "PRESERVATION_LETTER"
    | "PIP_DEMAND"
    | "OTHER";
  /** * `PDF` - Pdf
   * `DOCX` - Docx
   * `HTML` - Html
   * `SMS` - Sms
   * `EXTERNAL` - External */
  format?: "PDF" | "DOCX" | "HTML" | "SMS" | "EXTERNAL";
  /** * `GLOBAL` - Global
   * `FIRM` - Firm */
  scope?: "GLOBAL" | "FIRM";
  firm_id?: string | null;
  firm_name?: string | null;
  is_global?: boolean;
  is_default?: boolean;
  is_deleted?: boolean;
  deleted_at?: string | null;
};
export type TemplateFormat = "PDF" | "DOCX" | "HTML" | "SMS" | "EXTERNAL";
export type TemplateCategory =
  | "HIPAA_COVERSHEET"
  | "HIPAA_AUTHORIZATION"
  | "FOIA_LETTER"
  | "LIEN_NOTICE"
  | "RETAINER"
  | "LOR_GENERIC"
  | "LOR_FIRST_PARTY"
  | "LOR_THIRD_PARTY"
  | "LOR_HEALTH"
  | "WELCOME_LETTER"
  | "DROP_LETTER"
  | "JOINT_VENTURE_NOTICE"
  | "CLIENT_COMMUNICATION"
  | "MEDICAL_RECORDS_REQUEST"
  | "AFFIDAVIT"
  | "LETTER_OF_PROTECTION"
  | "PRESERVATION_LETTER"
  | "PIP_DEMAND"
  | "OTHER";
export type TemplateScope = "GLOBAL" | "FIRM";
export type ExternalSystem = "ADOBE_SIGN";
export type TemplateStatus = "ACTIVE" | "SUPERSEDED" | "ARCHIVED";
export type ReadTemplate = {
  id: string | string;
  name: string;
  display_name?: string | null;
  description?: string | null;
  format: TemplateFormat;
  category: TemplateCategory;
  scope: TemplateScope;
  firm_id?: (string | string) | null;
  attorney_id?: (string | string) | null;
  attorney_name?: string | null;
  body?: string | null;
  file_url?: string | null;
  external_system?: ExternalSystem;
  external_id?: string | null;
  preview_render_url?: string | null;
  preview_image_url?: string | null;
  sample_render_status?: string | null;
  status: TemplateStatus;
  is_default: boolean;
  version: number;
  version_group: string;
  deleted_at?: string | null;
  created_at: string;
  modified_at: string;
};
export type ReadTemplateResolution = {
  template?: ReadTemplate;
  source_tier: string;
};
export type SetDefaultSchema = {
  template_id: string | string;
};
export const {
  useAdminTemplatesListQuery,
  useAdminTemplatesCreateMutation,
  useAdminTemplatesRetrieveQuery,
  useAdminTemplatesPartialUpdateMutation,
  useAdminTemplatesSoftDeleteMutation,
  useAdminTemplatesResolveDefaultQuery,
  useAdminTemplatesSetDefaultMutation,
  useAdminTemplatesUnsetDefaultMutation,
} = injectedRtkApi;
